import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from '@mui/material';
import {
  bulkProductService,
  BloomStockItem,
  BloomStockItemKind,
  BloomStockLot,
  STORAGE_LOCATIONS,
  type StorageLocation,
  bloom_stock_status_from_item,
  bloom_stock_status_label,
  bloom_stock_status_style,
  merge_vendor_options,
} from '../services/bulkProductService';
import { parseBloomStockFilter, formatBloomStockImportNotice } from './bulkProducts.helpers';

function VendorMultiCombobox({
  id,
  label = 'Vendors',
  value,
  onChange,
  options,
  on_options_change,
  placeholder = 'Search or type a vendor, press Enter to add',
}: {
  id: string;
  label?: string;
  value: string[];
  onChange: (vendors: string[]) => void;
  options: string[];
  on_options_change?: (options: string[]) => void;
  placeholder?: string;
}) {
  const [input, set_input] = useState('');
  const [open, set_open] = useState(false);
  const normalized_value = useMemo(
    () => [...new Set(value.map((v) => v.trim()).filter(Boolean))],
    [value]
  );
  const filtered = useMemo(() => {
    const q = input.trim().toLowerCase();
    return options.filter(
      (opt) =>
        !normalized_value.some((v) => v.toLowerCase() === opt.toLowerCase()) &&
        (!q || opt.toLowerCase().includes(q))
    );
  }, [input, normalized_value, options]);

  const add_vendor = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (normalized_value.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      set_input('');
      return;
    }
    onChange([...normalized_value, trimmed]);
    if (!options.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
      on_options_change?.(merge_vendor_options(options, [], [trimmed]));
    }
    set_input('');
    set_open(false);
  };

  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      {normalized_value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
          {normalized_value.map((v) => (
            <span
              key={v}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.15rem 0.5rem',
                backgroundColor: '#edf2f7',
                borderRadius: '4px',
                fontSize: '0.875rem',
              }}
            >
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => onChange(normalized_value.filter((name) => name !== v))}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                  fontSize: '1rem',
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          id={id}
          value={input}
          onChange={(e) => {
            set_input(e.target.value);
            set_open(true);
          }}
          onFocus={() => set_open(true)}
          onBlur={() => {
            window.setTimeout(() => set_open(false), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (input.trim()) add_vendor(input);
              else if (filtered.length === 1) add_vendor(filtered[0]);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {open && filtered.length > 0 && (
          <ul
            role="listbox"
            style={{
              position: 'absolute',
              zIndex: 20,
              top: '100%',
              left: 0,
              right: 0,
              margin: '2px 0 0',
              padding: 0,
              listStyle: 'none',
              maxHeight: '10rem',
              overflowY: 'auto',
              backgroundColor: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
            }}
          >
            {filtered.slice(0, 20).map((opt) => (
              <li key={opt}>
                <button
                  type="button"
                  role="option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add_vendor(opt)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  {opt}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const emptyStockForm = {
  name: '',
  sku_number: '',
  vendor: '',
  vendors: [] as string[],
  item_kind: 'ingredient' as BloomStockItemKind,
  low_stock_threshold_cases: 0,
  ideal_order_cases: 0,
  description: '',
  initial_cases: 0,
};

const BulkProducts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stockFilter = parseBloomStockFilter(searchParams.get('filter'));

  const [stockItems, setStockItems] = useState<BloomStockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStock, setEditingStock] = useState<BloomStockItem | null>(null);
  const [modalError, setModalError] = useState('');
  const [stockForm, setStockForm] = useState(emptyStockForm);
  const [adjustingItem, setAdjustingItem] = useState<BloomStockItem | null>(null);
  const [adjustDirection, setAdjustDirection] = useState<'add' | 'reduce'>('add');
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [importNotice, setImportNotice] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const importInputRef = React.useRef<HTMLInputElement | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const [vendor_options, set_vendor_options] = useState<string[]>([]);
  const [vendor_report_name, set_vendor_report_name] = useState<string | null>(null);
  const [vendor_report_items, set_vendor_report_items] = useState<BloomStockItem[]>([]);
  const [vendor_report_loading, set_vendor_report_loading] = useState(false);
  const [lot_received_date, set_lot_received_date] = useState('');
  const [lot_expiry_date, set_lot_expiry_date] = useState('');
  const [lot_storage_location, set_lot_storage_location] = useState<StorageLocation>('Warehouse');
  const [expanded_lot_item_id, set_expanded_lot_item_id] = useState<number | null>(null);
  const [item_lots, set_item_lots] = useState<BloomStockLot[]>([]);
  const [item_lots_loading, set_item_lots_loading] = useState(false);
  const [item_lots_error, set_item_lots_error] = useState('');
  const [vendor_report_error, set_vendor_report_error] = useState('');

  useEffect(() => {
    bulkProductService
      .getVendorNames()
      .then((names) => set_vendor_options(names))
      .catch(() => set_vendor_options([]));
  }, []);

  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1');
    const urlPageSize = parseInt(searchParams.get('pageSize') || '20');
    const urlSearch = searchParams.get('search') || '';
    setCurrentPage(urlPage < 1 ? 1 : urlPage);
    setPageSize(urlPageSize < 1 ? 20 : urlPageSize);
    setSearchTerm(urlSearch);
    setDebouncedSearchTerm(urlSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > 0 && pageSize > 0) {
      loadBloomStock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchTerm, stockFilter]);

  useEffect(() => {
    if (currentPage > 0 && pageSize > 0) {
      const params = new URLSearchParams(searchParams);
      params.delete('tab');
      params.set('page', currentPage.toString());
      params.set('pageSize', pageSize.toString());
      if (searchTerm) {
        params.set('search', searchTerm);
      } else {
        params.delete('search');
      }
      if (stockFilter === 'low') {
        params.set('filter', 'low');
      } else {
        params.delete('filter');
      }
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, stockFilter]);

  const setStockFilter = (next: 'all' | 'low') => {
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    params.set('page', '1');
    if (next === 'low') {
      params.set('filter', 'low');
    } else {
      params.delete('filter');
    }
    setCurrentPage(1);
    setSearchParams(params);
  };

  const apiErrorMessage = (err: any) =>
    err.response?.data?.error?.message || err.message || 'An error occurred';

  const applyPagination = (total_pages: number, total: number, page: number) => {
    setTotalPages(total_pages);
    setTotalItems(total);
    if (page > total_pages && total_pages > 0) {
      setCurrentPage(1);
    }
  };

  const withListRequest = async (run: (signal: AbortSignal) => Promise<void>) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    try {
      setLoading(true);
      setError('');
      await run(abortController.signal);
    } catch (err: any) {
      if (err.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      setError(err.message);
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const loadBloomStock = async (page = currentPage, size = pageSize) => {
    await withListRequest(async (signal) => {
      const result = await bulkProductService.getBloomStockPaginated(
        page,
        size,
        debouncedSearchTerm.trim() || undefined,
        stockFilter
      );
      if (signal.aborted) return;
      setStockItems(result.items);
      applyPagination(result.pagination.totalPages, result.pagination.total, page);
    });
  };

  const handleSearch = async () => {
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    const validPage = Math.max(1, Math.min(newPage, totalPages || 1));
    setCurrentPage(validPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleStockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setModalError('');
      setError('');
      const vendors = stockForm.vendors || [];
      if (editingStock) {
        await bulkProductService.updateBloomStock(editingStock.id, {
          name: stockForm.name,
          sku_number: stockForm.sku_number,
          vendor: vendors[0],
          vendors,
          item_kind: stockForm.item_kind,
          low_stock_threshold_cases: Number(stockForm.low_stock_threshold_cases) || 0,
          ideal_order_cases: Number(stockForm.ideal_order_cases) || 0,
          description: stockForm.description.trim() || null,
        });
      } else {
        await bulkProductService.createBloomStock({
          name: stockForm.name,
          sku_number: stockForm.sku_number,
          vendor: vendors[0],
          vendors,
          item_kind: stockForm.item_kind,
          low_stock_threshold_cases: Number(stockForm.low_stock_threshold_cases) || 0,
          ideal_order_cases: Number(stockForm.ideal_order_cases) || 0,
          description: stockForm.description.trim() || null,
          initial_cases: Number(stockForm.initial_cases) || 0,
        });
      }
      set_vendor_options((prev) => merge_vendor_options(prev, vendors, []));
      setShowForm(false);
      setEditingStock(null);
      setStockForm(emptyStockForm);
      await loadBloomStock();
    } catch (err: any) {
      setModalError(apiErrorMessage(err));
    }
  };

  const handleEditStock = (item: BloomStockItem) => {
    setModalError('');
    setEditingStock(item);
    const vendors =
      item.vendors && item.vendors.length > 0
        ? item.vendors
        : item.vendor
          ? [item.vendor]
          : [];
    setStockForm({
      name: item.name,
      sku_number: item.sku_number,
      vendor: item.vendor || '',
      vendors,
      item_kind: item.item_kind || 'ingredient',
      low_stock_threshold_cases: item.low_stock_threshold_cases,
      ideal_order_cases: item.ideal_order_cases || 0,
      description: item.description || '',
      initial_cases: 0,
    });
    setShowForm(true);
  };

  const toggle_lot_expand = async (item: BloomStockItem) => {
    if (expanded_lot_item_id === item.id) {
      set_expanded_lot_item_id(null);
      set_item_lots([]);
      return;
    }
    set_expanded_lot_item_id(item.id);
    set_item_lots_loading(true);
    set_item_lots_error('');
    try {
      const lots = await bulkProductService.getBloomStockLots(item.id);
      set_item_lots(lots);
    } catch (err: any) {
      set_item_lots([]);
      set_item_lots_error(apiErrorMessage(err));
    } finally {
      set_item_lots_loading(false);
    }
  };

  const open_vendor_report = async (vendor_name: string) => {
    const vendor_label = vendor_name.trim();
    if (!vendor_label) return;
    set_vendor_report_name(vendor_label);
    set_vendor_report_items([]);
    set_vendor_report_error('');
    set_vendor_report_loading(true);
    try {
      const items = await bulkProductService.getBloomStockByVendor(vendor_label);
      set_vendor_report_items(items);
    } catch (err: any) {
      set_vendor_report_items([]);
      set_vendor_report_error(apiErrorMessage(err));
    } finally {
      set_vendor_report_loading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !confirm(
        'Delete this bulk product? If it has Bloom stock, the count will be reduced to 0 and the ledger will be kept (records are not deleted).'
      )
    ) {
      return;
    }

    try {
      setError('');
      const message = await bulkProductService.deleteBulkProduct(id);
      setImportNotice({ severity: 'success', message });
      await loadBloomStock();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openAdjust = (item: BloomStockItem, direction: 'add' | 'reduce') => {
    setAdjustingItem(item);
    setAdjustDirection(direction);
    setAdjustQuantity('');
    setAdjustReason('');
    const today = new Date().toISOString().slice(0, 10);
    set_lot_received_date(today);
    set_lot_expiry_date('');
    set_lot_storage_location('Warehouse');
    setModalError('');
  };

  const handleAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adjustingItem) return;
    const quantity = parseInt(adjustQuantity, 10);
    if (!Number.isInteger(quantity) || quantity <= 0) {
      setModalError('Enter a whole number of cases greater than 0');
      return;
    }
    if (adjustDirection === 'add' && lot_expiry_date && lot_expiry_date < lot_received_date) {
      setModalError('Expiry date must be on or after the received date');
      return;
    }
    const cases_adjusted = adjustDirection === 'add' ? quantity : -quantity;
    try {
      setModalError('');
      if (adjustDirection === 'add' && lot_received_date && lot_expiry_date) {
        await bulkProductService.receiveBloomStockLot({
          bulk_product_id: adjustingItem.id,
          cases: quantity,
          received_date: lot_received_date,
          expiry_date: lot_expiry_date,
          storage_location: lot_storage_location,
          reason: adjustReason || 'Lot receive',
        });
      } else {
        await bulkProductService.adjustBloomStock(
          adjustingItem.id,
          cases_adjusted,
          adjustReason || undefined
        );
      }
      setAdjustingItem(null);
      await loadBloomStock();
    } catch (err: any) {
      setModalError(apiErrorMessage(err));
    }
  };

  const handleImportFile = async (file: File) => {
    setImportNotice(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setImportNotice({ severity: 'error', message: 'Could not read the import file.' });
    };
    reader.onload = async () => {
      try {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        const importResult = await bulkProductService.importBloomStock(base64, file.name);
        const notice = formatBloomStockImportNotice({
          applied_count: importResult.applied.length,
          errors: importResult.errors,
        });
        await loadBloomStock();
        setImportNotice(notice);
      } catch (err: any) {
        setImportNotice({
          severity: 'error',
          message: apiErrorMessage(err) || 'Import failed',
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingStock(null);
    setStockForm(emptyStockForm);
    setError('');
    setModalError('');
  };

  const kindLabel = (kind?: string | null) => {
    if (kind === 'box') return 'Box';
    if (kind === 'container') return 'Container';
    if (kind === 'ingredient') return 'Ingredient';
    return '—';
  };

  const paginationBar = (
    <>
      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
            padding: '1rem 0',
            borderTop: '1px solid #e1e5e9',
          }}
        >
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}{' '}
            bulk products
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>Show:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                }}
              >
                Previous
              </button>

              <span style={{ fontSize: '0.875rem', color: '#6b7280', padding: '0 0.5rem' }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Bulk Products</h1>
        <p className="page-subtitle">
          Catalog of bulk products with cases on hand, reorder status, and lots
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {importNotice && (
        <Alert severity={importNotice.severity} sx={{ mb: 2 }} onClose={() => setImportNotice(null)}>
          {importNotice.message}
        </Alert>
      )}

      <div className="card">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem',
              gap: '0.75rem',
              flexWrap: 'wrap',
            }}
          >
            <h3>Bulk products</h3>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImportFile(file);
                    e.target.value = '';
                  }
                }}
              />
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => importInputRef.current?.click()}
              >
                Import
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  setEditingStock(null);
                  setStockForm(emptyStockForm);
                  setShowForm(true);
                }}
              >
                Add bulk product
              </button>
            </div>
          </div>

          <p style={{ color: '#6b7280', marginTop: 0 }}>
            On-hand cases come from receipts, adjustments, imports, and completed production orders.
            Click a vendor to open that vendor&apos;s SKU report. Descriptions may need updating when
            the same item is sourced from a different vendor.
          </p>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search bulk products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1, padding: '0.5rem', minWidth: '12rem' }}
              />
              <button
                className={`btn ${stockFilter === 'low' ? 'btn-primary' : 'btn-outline'}`}
                type="button"
                onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}
              >
                {stockFilter === 'low' ? 'Showing low stock' : 'Low stock only'}
              </button>
              {searchTerm && (
                <button className="btn btn-outline" type="button" onClick={() => setSearchTerm('')}>
                  Clear
                </button>
              )}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Spreadsheet columns: sku_number or container_name, and optionally vendor, ideal_order_cases
              (normal order quantity), reorder_threshold_cases, cases_adjusted, and reason. A
              container_name row creates a container in Bloom stock. cases_adjusted changes on-hand
              cases for an existing SKU. The whole file is validated first; nothing is imported if
              any row is wrong.
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading bulk products...</div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>SKU</th>
                      <th>Vendor</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>On Hand</th>
                      <th>Normal order (cases)</th>
                      <th>Reorder threshold</th>
                      <th>Status</th>
                      <th>Lot Expiry</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                          {stockFilter === 'low'
                            ? 'No bulk products are below their reorder number'
                            : 'No bulk products yet'}
                        </td>
                      </tr>
                    ) : (
                      stockItems.map((item) => {
                        const vendor_names =
                          item.vendors && item.vendors.length > 0
                            ? item.vendors
                            : item.vendor
                              ? [item.vendor]
                              : [];
                        const stock_status = bloom_stock_status_from_item(item);
                        const is_lot_expanded = expanded_lot_item_id === item.id;
                        return (
                          <React.Fragment key={item.id}>
                        <tr
                          style={item.is_low ? { backgroundColor: '#fff5f5' } : undefined}
                        >
                          <td>{item.name}</td>
                          <td>{item.sku_number}</td>
                          <td>
                            {vendor_names.length > 0 ? (
                              <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {vendor_names.map((v, idx) => (
                                  <React.Fragment key={`${item.id}-${v}-${idx}`}>
                                    {idx > 0 ? ', ' : null}
                                    <button
                                      type="button"
                                      className="btn btn-link"
                                      style={{ padding: 0, textAlign: 'left' }}
                                      onClick={() => open_vendor_report(v)}
                                    >
                                      {v}
                                    </button>
                                  </React.Fragment>
                                ))}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td>{item.description?.trim() || '—'}</td>
                          <td>{kindLabel(item.item_kind)}</td>
                          <td>{item.cases_on_hand}</td>
                          <td>{item.ideal_order_cases > 0 ? item.ideal_order_cases : '—'}</td>
                          <td>
                            {item.low_stock_threshold_cases > 0
                              ? item.low_stock_threshold_cases
                              : '—'}
                          </td>
                          <td>
                            <span style={bloom_stock_status_style(stock_status)}>
                              {bloom_stock_status_label(stock_status)}
                            </span>
                          </td>
                          <td>
                            {(item.expired_lot_cases ?? 0) > 0 && (
                              <span style={{ color: '#c53030', marginRight: '0.35rem' }}>
                                Expired: {item.expired_lot_cases}
                              </span>
                            )}
                            {(item.expiring_soon_lot_cases ?? 0) > 0 && (
                              <span style={{ color: '#c05621' }}>
                                Soon: {item.expiring_soon_lot_cases}
                              </span>
                            )}
                            {(item.expired_lot_cases ?? 0) === 0 &&
                              (item.expiring_soon_lot_cases ?? 0) === 0 &&
                              '—'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'nowrap' }}>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => toggle_lot_expand(item)}
                                style={{
                                  width: '6.5rem',
                                  flex: '0 0 6.5rem',
                                  whiteSpace: 'nowrap',
                                  textAlign: 'center',
                                }}
                              >
                                {is_lot_expanded ? 'Hide lots' : 'Lots'}
                              </button>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => openAdjust(item, 'add')}
                              >
                                Add
                              </button>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => openAdjust(item, 'reduce')}
                              >
                                Reduce
                              </button>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => handleEditStock(item)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-small"
                                type="button"
                                onClick={() => handleDelete(item.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {is_lot_expanded && (
                          <tr>
                            <td colSpan={11} style={{ backgroundColor: '#f8fafc', padding: '0.75rem 1rem' }}>
                              <strong>Lots (FIFO: earliest expiry first)</strong>
                              {item_lots_loading ? (
                                <div style={{ marginTop: '0.5rem' }}>Loading…</div>
                              ) : item_lots_error ? (
                                <div style={{ marginTop: '0.5rem', color: '#c53030' }}>{item_lots_error}</div>
                              ) : item_lots.length === 0 ? (
                                <div style={{ marginTop: '0.5rem', color: '#6b7280' }}>
                                  No open lots — on-hand may be from manual adjust/import only. Use
                                  Add with received/expiry/location to create lots.
                                </div>
                              ) : (
                                <table
                                  className="table"
                                  style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}
                                >
                                  <thead>
                                    <tr>
                                      <th>Received</th>
                                      <th>Expiry</th>
                                      <th>Location</th>
                                      <th>Cases</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item_lots.map((lot) => (
                                      <tr key={lot.id}>
                                        <td>{lot.received_date}</td>
                                        <td>{lot.expiry_date}</td>
                                        <td>{lot.storage_location}</td>
                                        <td>{lot.cases_on_hand}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {paginationBar}
            </>
          )}
        </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingStock ? 'Edit bulk product' : 'Add bulk product'}</h3>
              <button className="btn btn-small" onClick={resetForm}>
                ×
              </button>
            </div>
            {modalError && (
              <div
                style={{
                  margin: '1rem 1.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                }}
              >
                {modalError}
              </div>
            )}
            <form onSubmit={handleStockSubmit}>
              <div className="form-group">
                <label htmlFor="stock_name">Name *</label>
                <input
                  type="text"
                  id="stock_name"
                  value={stockForm.name}
                  onChange={(e) => setStockForm({ ...stockForm, name: e.target.value })}
                  required
                  maxLength={100}
                />
              </div>
              <div className="form-group">
                <label htmlFor="stock_sku">SKU Number *</label>
                <input
                  type="text"
                  id="stock_sku"
                  value={stockForm.sku_number}
                  onChange={(e) => setStockForm({ ...stockForm, sku_number: e.target.value })}
                  required
                  maxLength={50}
                />
              </div>
              <VendorMultiCombobox
                id="stock_vendors_text"
                value={stockForm.vendors}
                onChange={(vendors) => setStockForm({ ...stockForm, vendors })}
                options={vendor_options}
                on_options_change={set_vendor_options}
              />
              <div className="form-group">
                <label htmlFor="stock_kind">Type *</label>
                <select
                  id="stock_kind"
                  value={stockForm.item_kind}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      item_kind: e.target.value as BloomStockItemKind,
                    })
                  }
                  required
                >
                  <option value="ingredient">Ingredient</option>
                  <option value="box">Box</option>
                  <option value="container">Container</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="stock_description">Description</label>
                <textarea
                  id="stock_description"
                  value={stockForm.description}
                  onChange={(e) => setStockForm({ ...stockForm, description: e.target.value })}
                  maxLength={500}
                  rows={3}
                />
                <small>
                  Descriptions may need updating when this item is sourced from a different vendor.
                </small>
              </div>
              <div className="form-group">
                <label htmlFor="stock_ideal_order">Normal order quantity (cases)</label>
                <input
                  type="number"
                  id="stock_ideal_order"
                  min={0}
                  step={1}
                  value={stockForm.ideal_order_cases}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      ideal_order_cases: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
                <small>Ideal case purchase order amount. 0 means not set.</small>
              </div>
              <div className="form-group">
                <label htmlFor="stock_threshold">Reorder threshold (cases)</label>
                <input
                  type="number"
                  id="stock_threshold"
                  min={0}
                  step={1}
                  value={stockForm.low_stock_threshold_cases}
                  onChange={(e) =>
                    setStockForm({
                      ...stockForm,
                      low_stock_threshold_cases: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
                <small>0 means no low-stock alert.</small>
              </div>
              {!editingStock && (
                <div className="form-group">
                  <label htmlFor="stock_initial">Starting count (cases)</label>
                  <input
                    type="number"
                    id="stock_initial"
                    min={0}
                    step={1}
                    value={stockForm.initial_cases}
                    onChange={(e) =>
                      setStockForm({
                        ...stockForm,
                        initial_cases: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingStock ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {adjustingItem && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {adjustDirection === 'add' ? 'Add' : 'Reduce'} {adjustingItem.name}
              </h3>
              <button className="btn btn-small" onClick={() => setAdjustingItem(null)}>
                ×
              </button>
            </div>
            {modalError && (
              <div
                style={{
                  margin: '1rem 1.5rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '4px',
                  color: '#c33',
                }}
              >
                {modalError}
              </div>
            )}
            <form onSubmit={handleAdjustSubmit}>
              <p style={{ margin: '0 1.5rem 1rem' }}>
                On hand: <strong>{adjustingItem.cases_on_hand}</strong> cases
                {adjustingItem.low_stock_threshold_cases > 0
                  ? ` · reorder when below ${adjustingItem.low_stock_threshold_cases}`
                  : ''}
              </p>
              <div className="form-group">
                <label htmlFor="adjust_qty">Cases *</label>
                <input
                  type="number"
                  id="adjust_qty"
                  min={1}
                  step={1}
                  value={adjustQuantity}
                  onChange={(e) => setAdjustQuantity(e.target.value)}
                  required
                />
              </div>
              {adjustDirection === 'add' && (
                <>
                  <p style={{ margin: '0 1.5rem 0.5rem', fontSize: '0.85rem', color: '#4b5563' }}>
                    Optional: set received, expiry, and storage to record a lot (like finished SKU
                    batches). Leave expiry blank for a simple count adjust.
                  </p>
                  <div className="form-group">
                    <label htmlFor="lot_received">Received date</label>
                    <input
                      type="date"
                      id="lot_received"
                      value={lot_received_date}
                      onChange={(e) => set_lot_received_date(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lot_expiry">Expiry date</label>
                    <input
                      type="date"
                      id="lot_expiry"
                      value={lot_expiry_date}
                      onChange={(e) => set_lot_expiry_date(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lot_storage">Storage location</label>
                    <select
                      id="lot_storage"
                      value={lot_storage_location}
                      onChange={(e) =>
                        set_lot_storage_location(e.target.value as BulkStorageLocation)
                      }
                    >
                      {STORAGE_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              <div className="form-group">
                <label htmlFor="adjust_reason">Reason</label>
                <input
                  type="text"
                  id="adjust_reason"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  maxLength={255}
                  placeholder="Optional"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAdjustingItem(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {adjustDirection === 'add' ? 'Add' : 'Reduce'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {vendor_report_name && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '42rem' }}>
            <div className="modal-header">
              <h3>Vendor report: {vendor_report_name}</h3>
              <button
                className="btn btn-small"
                type="button"
                onClick={() => set_vendor_report_name(null)}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1rem 1.5rem 1.5rem' }}>
              {vendor_report_loading ? (
                <div>Loading…</div>
              ) : vendor_report_error ? (
                <div style={{ color: '#c53030' }}>{vendor_report_error}</div>
              ) : vendor_report_items.length === 0 ? (
                <div style={{ color: '#6b7280' }}>No SKUs for this vendor.</div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>Type</th>
                        <th>On Hand</th>
                        <th>Normal order (cases)</th>
                        <th>Reorder threshold</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendor_report_items.map((row) => {
                        const status = bloom_stock_status_from_item(row);
                        return (
                          <tr key={row.id}>
                            <td>{row.name}</td>
                            <td>{row.sku_number}</td>
                            <td>{row.description?.trim() || '—'}</td>
                            <td>{kindLabel(row.item_kind)}</td>
                            <td>{row.cases_on_hand}</td>
                            <td>{row.ideal_order_cases > 0 ? row.ideal_order_cases : '—'}</td>
                            <td>
                              {row.low_stock_threshold_cases > 0 ? row.low_stock_threshold_cases : '—'}
                            </td>
                            <td>
                              <span style={bloom_stock_status_style(status)}>
                                {bloom_stock_status_label(status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkProducts;
